import { Edit, MoreVert } from '@mui/icons-material';
import CircleIcon from '@mui/icons-material/Circle';
import { IconButton, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import { t } from 'i18next';
import _ from 'lodash';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { License } from '../../../../types/licenses.types';
import useMapProductNames from '../licenses/use-map-product-names';
import LicenseItemMenu from '../licenses/license-item-menu';

interface TenantLicenseItemProps {
  license: License;

  mapProductName(product: string): string;

  handleOpenAdminEditLicenseDialog(license: License): () => void;

  handleOpenDeleteLicenseDialog(license: License): () => void;
}

const TenantLicenseItem: React.FC<TenantLicenseItemProps> = ({
  license,
  mapProductName,
  handleOpenAdminEditLicenseDialog,
  handleOpenDeleteLicenseDialog,
}) => {
  const { classes, cx } = useStyles();

  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMenu = (el: any) => {
    setMenuOpen(true);
    setAnchorEl(el);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  const { product, isExpired, expiresAt, isActive, stripeSubscriptionId, linkedinAccountId, linkedinAccount, assignedUser } = license;

  const isStripeLicense = !!stripeSubscriptionId;

  /**
   * Which LinkedIn account holds this seat — and, above it, which person works under it.
   *
   * Three distinct states, and they must stay distinguishable: assigned, free, and
   * assigned to an account that no longer exists. The last one used to be invisible —
   * the seat simply counted as occupied while nothing pointed at it, which is how the
   * tenant silently lost a seat it had paid for.
   *
   * The person is the newer half (Ticket 39c30fdf, 09.08.: „man sieht nach wie vor nicht,
   * welcher Nutzer zu welcher Lizenz zugebucht ist"). A LinkedIn address answers *which
   * seat*, not *whose*; the two are only the same when the account happens to be named
   * after its owner. `assignedUser` may be null while the account exists — a removed user
   * must not blank out the seat.
   */
  const getAssignment = () => {
    if (linkedinAccount) {
      const label = linkedinAccount.email || linkedinAccount.name || linkedinAccount._id;
      return (
        <Stack>
          {assignedUser && (
            <Typography className={classes.secondaryText}>
              {assignedUser.name || assignedUser.email}
            </Typography>
          )}
          <Typography className={cx(classes.secondaryText, assignedUser && classes.assignmentAccount)}>
            {label}
          </Typography>
        </Stack>
      );
    }
    if (linkedinAccountId) {
      return (
        <Typography className={cx(classes.secondaryText, classes.danglingAssignment)}>
          {t('licenseAssignmentDangling')}
        </Typography>
      );
    }
    return <Typography className={classes.secondaryText}>{t('licenseAssignmentFree')}</Typography>;
  };

  const getLicenseStatus = () => {
    if (isExpired) {
      return (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <CircleIcon className={cx(classes.statusIcon, 'expired')} />
          <Typography className={classes.secondaryText}>{t('inactive')}</Typography>
        </Stack>
      );
    }
    if (!isActive) {
      return (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <CircleIcon className={cx(classes.statusIcon, 'canceled')} />
          <Typography className={classes.secondaryText}>{t('canceled')}</Typography>
        </Stack>
      );
    }
    return (
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <CircleIcon className={cx(classes.statusIcon, 'active')} />
        <Typography className={classes.secondaryText}>{t('active')}</Typography>
      </Stack>
    );
  };

  const getLicenseExpirationDate = () => {
    if (isExpired) {
      return `${t('expiredAt')}: ${formatDateTime(new Date(expiresAt!).toISOString(), 'P')} ${!isStripeLicense ? `(${t('bookedManually')})` : ''}`;
    }
    if (!isActive) {
      return `${t('expiresAt')}: ${formatDateTime(new Date(expiresAt!).toISOString(), 'P')}`;
    }
    if (!isStripeLicense) {
      return `${t('expiresAt')}: ${formatDateTime(new Date(expiresAt!).toISOString(), 'P')} (${t('bookedManually')})`;
    }
    return `${t('nextPayment')}: ${formatDateTime(new Date(expiresAt!).toISOString(), 'P')}`;
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <PaiLink
            text={mapProductName(product)}
            size={'md'}
            variant={'primary'}
            underline={'hover'}
            IconComponent={<Edit />}
            iconPlacement={'end'}
            action={handleOpenAdminEditLicenseDialog(license)}
          />
        </TableCell>
        <TableCell>{expiresAt ? getLicenseExpirationDate() : '-'}</TableCell>
        <TableCell>{getAssignment()}</TableCell>
        <TableCell>{getLicenseStatus()}</TableCell>
        <TableCell>
          <IconButton
            className={classes.menuIconButton}
            size={'small'}
            onClick={(event: React.MouseEvent<HTMLElement>) => handleOpenMenu(event.currentTarget)}
          >
            <MoreVert />
          </IconButton>
        </TableCell>
      </TableRow>

      <LicenseItemMenu
        anchorEl={anchorEl}
        menuOpen={menuOpen}
        canDelete={!isStripeLicense}
        handleOpenEditLicenseDialog={handleOpenAdminEditLicenseDialog(license)}
        handleOpenDeleteLicenseDialog={handleOpenDeleteLicenseDialog(license)}
        handleCloseMenu={handleCloseMenu}
      />
    </>
  );
};

interface Props {
  tenantLicenses: License[];
  tenantLicensesTotal: number;
  tenantLicensesPaginationLoading: boolean;
  tenantLicensesItemsPerPage: number;
  tenantLicensesPage: number;

  tenantLicensesHandleChangePage(event: unknown, newPage: number): void;

  handleOpenAdminCreateLicenseDialog(): void;

  handleOpenAdminEditLicenseDialog(license: License): () => void;

  handleOpenDeleteLicenseDialog(license: License): () => void;
}

const TenantLicensesTable: React.FC<Props> = ({
  tenantLicenses,
  tenantLicensesTotal,
  tenantLicensesPaginationLoading,
  tenantLicensesItemsPerPage,
  tenantLicensesPage,
  tenantLicensesHandleChangePage,
  handleOpenAdminCreateLicenseDialog,
  handleOpenAdminEditLicenseDialog,
  handleOpenDeleteLicenseDialog,
}) => {
  const { classes } = useStyles();

  const mapProductName = useMapProductNames();

  return (
    <Stack gap={2}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '24%' }}>{t('product')}</TableCell>
            <TableCell sx={{ width: '30%' }}>{t('duration')}</TableCell>
            <TableCell sx={{ width: '26%' }}>{t('licenseAssignedTo')}</TableCell>
            <TableCell sx={{ width: '20%' }}>{t('status')}</TableCell>
            <TableCell sx={{ width: '28px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!_.isEmpty(tenantLicenses) ? (
            tenantLicensesPaginationLoading ? (
              tenantLicenses.map((license) => (
                <TableRow key={license._id}>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="circular" width={'24px'} height={'24px'} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              tenantLicenses.map((license) => (
                <TenantLicenseItem
                  key={license._id}
                  license={license}
                  mapProductName={mapProductName}
                  handleOpenAdminEditLicenseDialog={handleOpenAdminEditLicenseDialog}
                  handleOpenDeleteLicenseDialog={handleOpenDeleteLicenseDialog}
                />
              ))
            )
          ) : (
            <TableRow>
              <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                {t('noLicensesFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
        <PaiButton text={t('addLicense')} size={'small'} border={'rounded'} action={handleOpenAdminCreateLicenseDialog} />
        <PaiPagination
          total={tenantLicensesTotal}
          itemsPerPage={tenantLicensesItemsPerPage}
          page={tenantLicensesPage}
          handleChangePage={tenantLicensesHandleChangePage}
          hidden={tenantLicensesTotal <= tenantLicensesItemsPerPage}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  table: {
    border: `1px solid ${theme.palette.divider}`,
    '& th': {
      border: '1px solid #ddd',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      background: theme.palette.greys.lightest,
      fontWeight: 'bold',
    },
    '& td, th': {
      border: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
  },
  menuIconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.text.secondary,
    },
  },
  secondaryText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  danglingAssignment: {
    color: theme.palette.warning.main,
    fontStyle: 'italic',
  },
  // Only applied when a person is named above it: the LinkedIn address then answers a
  // follow-up question ("under which seat?") and should not compete with the name.
  assignmentAccount: {
    fontSize: theme.typography.pxToRem(12),
    opacity: 0.75,
  },
  statusIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.primary,
    '&.expired': {
      color: theme.palette.error.main,
    },
    '&.canceled': {
      color: theme.palette.warning.main,
    },
    '&.active': {
      color: theme.palette.success.main,
    },
  },
}));

export default TenantLicensesTable;
